import React, { useEffect, useRef, useState } from 'react';
import {
  getAuth,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  sendEmailVerification
} from 'firebase/auth';
import { TextField, Button, Typography, Alert, Box, Divider, CircularProgress } from '@mui/material';
import { authRecaptchaSx } from '../styles/secondaryMsiStyles';
import { IceButton } from '../styles/authStyles';
import AuthCardShell from './AuthCardShell';

// Formats for display: keeps a leading '+' as-is, else assumes US and
// formats as xxx-xxx-xxxx.
function formatPhoneNumber(value) {
  const trimmed = value.trim();
  if (trimmed.startsWith('+')) {
    return '+' + trimmed.slice(1).replace(/\D/g, '');
  } else {
    const digits = trimmed.replace(/\D/g, '').substring(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
}

// Firebase only lets a recently signed-in account add a second factor.
const RECENT_LOGIN_MESSAGE = 'For your security, sign in again before setting up SMS verification. '
  + 'Choose Sign out below, then sign back in and enter your phone number right away.';

const stepLabelSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};

function MFAEnrollment() {
  const [phoneInput, setPhoneInput] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshMessage, setRefreshMessage] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [busy, setBusy] = useState('');

  const auth = getAuth();
  const recaptchaVerifierRef = useRef(null);

  const clearRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
  };

  useEffect(() => clearRecaptchaVerifier, []);

  const handleSendVerificationEmail = async () => {
    setError('');
    try {
      const user = auth.currentUser;
      await sendEmailVerification(user);
      setSuccess("Verification email sent! Please check your inbox and verify your email, then click 'Refresh User Status'.");
      setEmailSent(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRefreshUser = async () => {
    setError('');
    setRefreshMessage('');
    try {
      const user = auth.currentUser;
      await user.reload();
      if (user.emailVerified) {
        setRefreshMessage("Email verified. You may now enroll MFA.");
      } else {
        setError("Email still unverified. Please check your inbox and verify your email.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendCode = async () => {
    setError('');
    setSuccess('');
    setRefreshMessage('');
    try {
      const user = auth.currentUser;
      if (!user) {
        setError("User not signed in");
        return;
      }
      await user.reload();
      if (!user.emailVerified) {
        const errMsg = "Your email is not verified. Please verify your email before enrolling MFA.";
        setError(errMsg);
        return;
      }

      const mfaSession = await multiFactor(user).getSession();

      clearRecaptchaVerifier();
      const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container-enroll', {
        size: 'invisible',
      });
      recaptchaVerifierRef.current = recaptchaVerifier;
      await recaptchaVerifier.render();

      const trimmedInput = phoneInput.trim();
      let e164Phone;
      if (trimmedInput.startsWith('+')) {
        // Assume it's international: remove non-digit characters (keeping the leading '+')
        e164Phone = '+' + trimmedInput.slice(1).replace(/\D/g, '');
        // Optionally, validate length (E.164 numbers can be up to 15 digits)
        if (e164Phone.length < 10 || e164Phone.length > 16) {
          setError("Please enter a valid international phone number.");
          return;
        }
      } else {
        // Assume US number: ensure 10 digits.
        const rawDigits = trimmedInput.replace(/\D/g, '');
        if (rawDigits.length !== 10) {
          setError("Please enter a 10-digit US phone number or an international number starting with +.");
          return;
        }
        e164Phone = `+1${rawDigits}`;
      }
      const phoneInfoOptions = {
        phoneNumber: e164Phone,
        session: mfaSession
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const vid = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
      setVerificationId(vid);
      setSentTo(e164Phone);
    } catch (err) {
      clearRecaptchaVerifier();
      if (err.code === 'auth/captcha-check-failed' && auth.settings.appVerificationDisabledForTesting) {
        setError("App verification is disabled for testing in this environment. Enable real reCAPTCHA before using SMS MFA.");
        return;
      }
      if (err.code === 'auth/invalid-app-credential' && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        setError("Firebase rejected the phone auth verifier for localhost. Test SMS MFA from a deployed domain or use Firebase test phone numbers locally.");
        return;
      }
      if (err.code === 'auth/requires-recent-login') {
        setError(RECENT_LOGIN_MESSAGE);
        return;
      }
      setError(err.message);
    }
  };

  const handleEnroll = async () => {
    setError('');
    setSuccess('');
    try {
      const user = auth.currentUser;
      if (!verificationId || !verificationCode) {
        setError("Please request a verification code and enter the SMS code.");
        return;
      }
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const mfaAssertion = PhoneMultiFactorGenerator.assertion(credential);
      await multiFactor(user).enroll(mfaAssertion, "Phone Number");

      setSuccess("Two-step verification is on. Please sign in again.");
      clearRecaptchaVerifier();

      // After a brief delay, sign out the user so that the Login screen is shown.
      setTimeout(async () => {
        await auth.signOut();
      }, 2000);
    } catch (err) {
      setError(err.code === 'auth/requires-recent-login' ? RECENT_LOGIN_MESSAGE : err.message);
    }
  };

  const runBusy = (key, action) => async () => {
    if (busy) return;
    setBusy(key);
    try {
      await action();
    } finally {
      setBusy('');
    }
  };

  const handleUseDifferentNumber = () => {
    clearRecaptchaVerifier();
    setVerificationId(null);
    setVerificationCode('');
    setSentTo('');
    setError('');
  };

  const email = auth.currentUser?.email || '';
  const emailVerified = Boolean(auth.currentUser?.emailVerified);
  const sentToDigits = sentTo.replace(/\D/g, '');

  return (
    <AuthCardShell
      title="Welcome to Alphamizer"
      subtitle={email ? `Set up two-step verification for ${email}.` : 'Set up two-step verification.'}
    >
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {refreshMessage && <Alert severity="success">{refreshMessage}</Alert>}
      {auth.settings.appVerificationDisabledForTesting && (
        <Alert severity="warning">
          SMS MFA is using Firebase test mode in this environment. Real text delivery will not work.
        </Alert>
      )}

      {!emailVerified && (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Alert severity="info">Verify your email address first.</Alert>
          <Button variant="outlined" onClick={handleSendVerificationEmail}>
            {emailSent ? 'Resend verification email' : 'Send verification email'}
          </Button>
          <Button variant="text" onClick={handleRefreshUser}>
            I&apos;ve verified my email
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'grid', gap: 1 }}>
        <Typography sx={stepLabelSx}>1 · Mobile number</Typography>
        <TextField
          inputProps={{ 'aria-label': 'Phone Number' }}
          type="tel"
          autoComplete="tel"
          value={phoneInput}
          onChange={(e) => setPhoneInput(formatPhoneNumber(e.target.value))}
          fullWidth
          placeholder="555-555-5555"
          disabled={Boolean(verificationId) || Boolean(busy)}
        />
        {!verificationId && (
          <IceButton
            variant="contained"
            onClick={runBusy('send', handleSendCode)}
            disabled={Boolean(busy) || !phoneInput.trim()}
            startIcon={busy === 'send' ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {busy === 'send' ? 'Sending code…' : 'Send Verification Code'}
          </IceButton>
        )}
      </Box>

      {verificationId && (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Typography sx={stepLabelSx}>{`2 · Code sent to •••${sentToDigits.slice(-4)}`}</Typography>
          <TextField
            inputProps={{ 'aria-label': 'Verification Code' }}
            placeholder="6-digit code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            fullWidth
            disabled={Boolean(busy)}
          />
          <IceButton
            variant="contained"
            onClick={runBusy('enroll', handleEnroll)}
            disabled={Boolean(busy) || !verificationCode.trim()}
            startIcon={busy === 'enroll' ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {busy === 'enroll' ? 'Verifying…' : 'Verify'}
          </IceButton>
          <Button variant="text" size="small" onClick={handleUseDifferentNumber} disabled={Boolean(busy)}>
            Use a different number
          </Button>
        </Box>
      )}

      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant="text" size="small" onClick={() => auth.signOut()}>
          Sign out
        </Button>
        <Box id="recaptcha-container-enroll" sx={authRecaptchaSx} />
      </Box>
    </AuthCardShell>
  );
}

export default MFAEnrollment;
